import { Box, Button, FormControl, FormLabel, MenuItem, Modal, Select, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useNotification from "../../hooks/useNotification";
import employeeService from "../../services/employee";
import mysteryShopperService from "../../services/mysteryShopper";
import queryDefault from "../../utils/queryDefault";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const getYearOptions = () => {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) => thisYear - index);
};

const imageModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  maxHeight: "90vh",
  overflowY: "auto" as const,
};

interface ImagePreviewData {
  title: string;
  images: string[];
}

interface DivisionSummary {
  division_name: string;
  total: number;
}

interface CategorySummary {
  total: number;
  divisions: DivisionSummary[];
}

interface MysteryShopperChartSummary {
  average_final_score: number;
  below_average: CategorySummary;
  same_or_more_than_average: CategorySummary;
}

export default function ListMysteryShopperPage() {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const notification = useNotification();

  const [pageSize, setPageSize] = useState<number>(10);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [openAssignEmployees, setOpenAssignEmployees] = useState(false);
  const [imagePreviewData, setImagePreviewData] = useState<ImagePreviewData>({
    title: "",
    images: [],
  });
  const [selectedMysteryShopperId, setSelectedMysteryShopperId] = useState<number | null>(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [options, setOptions] = useState<{
    month: number;
    year: number;
  }>({
    month: currentMonth,
    year: currentYear,
  });

  const filterForm = useForm({
    defaultValues: {
      month: currentMonth,
      year: currentYear,
    },
  });

  const listMysteryShoppers = useQuery(
    ["mystery-shopper-list", options],
    ({ queryKey }) => mysteryShopperService.getMysteryShopperList(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const onFilter = (values: any) => {
    setOptions({
      month: Number(values.month),
      year: Number(values.year),
    });
  };

  const employeeOptionsQuery = useQuery(
    ["employee-options-by-division", selectedDivisionId],
    () => employeeService.getEmployee({ divisionid: selectedDivisionId }),
    {
      enabled: openAssignEmployees && !!selectedDivisionId,
      ...queryDefault,
    }
  );

  const assignEmployeesMutation = useMutation(
    "assign-mystery-shopper-employees",
    mysteryShopperService.assignMysteryShopperEmployees,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Employees assigned successfully",
          type: "success",
          position: "top",
        });
        setOpenAssignEmployees(false);
        setSelectedMysteryShopperId(null);
        setSelectedDivisionId(null);
        setSelectedEmployeeIds([]);
        listMysteryShoppers.refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to assign employees",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const getEmployeeDisplayName = (employee: any) =>
    employee?.name || employee?.employee_name || employee?.username || `Employee #${employee?.id ?? "-"}` || employee;

  const openAssignEmployeesModal = (row: any) => {
    const existingEmployees = Array.isArray(row?.employees) ? row.employees : [];
    const mappedIds = existingEmployees
      .map((employee: any) => Number(employee?.id ?? employee))
      .filter((id: number) => !Number.isNaN(id));

    setSelectedMysteryShopperId(Number(row.id));
    setSelectedDivisionId(Number(row.division_id));
    setSelectedEmployeeIds(mappedIds);
    setOpenAssignEmployees(true);
  };

  const closeAssignEmployeesModal = () => {
    if (assignEmployeesMutation.isLoading) {
      return;
    }
    setOpenAssignEmployees(false);
    setSelectedMysteryShopperId(null);
    setSelectedDivisionId(null);
    setSelectedEmployeeIds([]);
  };

  const onSaveAssignEmployees = () => {
    if (!selectedMysteryShopperId) {
      return;
    }

    assignEmployeesMutation.mutate({
      mystery_shopper_id: selectedMysteryShopperId,
      employee_ids: selectedEmployeeIds,
    });
  };

  const openImagePreviewModal = (title: string, images: (string | null)[]) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    setImagePreviewData({
      title,
      images: images
        .filter((img): img is string => img !== null && img !== undefined)
        .map((img) => (img.startsWith("http") ? img : `${backendUrl}${img}`)),
    });
    setOpenImagePreview(true);
  };

  const getMediaUrl = (path: string) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    return path.startsWith("http") ? path : `${backendUrl}${path}`;
  };

  const rows = get(listMysteryShoppers, "data.data", []);
  const employeeOptions = get(employeeOptionsQuery, "data.data", []);
  const chartSummary = get(
    listMysteryShoppers,
    "data.chart_summary",
    null
  ) as MysteryShopperChartSummary | null;

  const belowAverageTotal = Number(get(chartSummary, "below_average.total", 0));
  const sameOrMoreTotal = Number(
    get(chartSummary, "same_or_more_than_average.total", 0)
  );
  const totalCompared = belowAverageTotal + sameOrMoreTotal;
  const belowAveragePercent = totalCompared > 0 ? (belowAverageTotal / totalCompared) * 100 : 0;
  const sameOrMorePercent = totalCompared > 0 ? (sameOrMoreTotal / totalCompared) * 100 : 0;

  const renderDivisionTooltip = (
    title: string,
    divisions: DivisionSummary[],
    categoryTotal: number
  ) => (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {divisions.length > 0 ? (
        divisions.map((division) => {
          const percent =
            categoryTotal > 0
              ? ((division.total / categoryTotal) * 100).toFixed(2)
              : "0.00";

          return (
            <Typography key={division.division_name} variant="caption" sx={{ display: "block" }}>
              {division.division_name}: {division.total} ({percent}%)
            </Typography>
          );
        })
      ) : (
        <Typography variant="caption">No division detail.</Typography>
      )}
    </Box>
  );

  return (
    <MainLayout>
      <Helmet>
        <title>Mystery Shopper</title>
      </Helmet>

      <Breadcrumb
        label="Mystery Shopper"
        breadcrumbs={[{ label: "Mystery Shopper", href: "/app/mystery-shopper" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[4, 5, 12]}>
        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <FormLabel>Month</FormLabel>
              <Controller
                name="month"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <FormLabel>Year</FormLabel>
              <Controller
                name="year"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {getYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <Box sx={{ alignSelf: "end" }}>
              <Button variant="outlined" onClick={filterForm.handleSubmit(onFilter)}>
                Apply Filter
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          {chartSummary && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Mystery Shopper Chart Summary
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: "grey.100",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Average Final Score
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {Number(get(chartSummary, "average_final_score", 0)).toFixed(2)}
                  </Typography>
                </Box>

                <Tooltip
                  title={renderDivisionTooltip(
                    "Below Average Divisions",
                    get(chartSummary, "below_average.divisions", []),
                    belowAverageTotal
                  )}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "error.lighter",
                      cursor: "help",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Below Average
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {belowAverageTotal}
                    </Typography>
                  </Box>
                </Tooltip>

                <Tooltip
                  title={renderDivisionTooltip(
                    "Same or Above Average Divisions",
                    get(chartSummary, "same_or_more_than_average.divisions", []),
                    sameOrMoreTotal
                  )}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "success.lighter",
                      cursor: "help",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Same or Above Average
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {sameOrMoreTotal}
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Distribution
              </Typography>
              <Stack direction="row" sx={{ width: "100%", height: 18, borderRadius: 1, overflow: "hidden" }}>
                <Tooltip
                  title={renderDivisionTooltip(
                    `Below Average (${belowAverageTotal})`,
                    get(chartSummary, "below_average.divisions", []),
                    belowAverageTotal
                  )}
                  arrow
                >
                  <Box
                    sx={{
                      width: `${belowAveragePercent}%`,
                      minWidth: belowAverageTotal > 0 ? 24 : 0,
                      bgcolor: "error.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>

                <Tooltip
                  title={renderDivisionTooltip(
                    `Same or Above Average (${sameOrMoreTotal})`,
                    get(chartSummary, "same_or_more_than_average.divisions", []),
                    sameOrMoreTotal
                  )}
                  arrow
                >
                  <Box
                    sx={{
                      width: `${sameOrMorePercent}%`,
                      minWidth: sameOrMoreTotal > 0 ? 24 : 0,
                      bgcolor: "success.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
            </Box>
          )}

          <DataGrid
            autoHeight
            loading={listMysteryShoppers.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={rows}
            rowCount={rows.length}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              { field: "shopper_name", headerName: "Shopper Name", minWidth: 150, flex: 1 },
              { field: "shopper_email", headerName: "Shopper Email", minWidth: 180, flex: 1 },
              { field: "shopper_phone", headerName: "Shopper Phone", minWidth: 140 },
              {
                field: "visit_date", headerName: "Visit Date", minWidth: 120, renderCell: (params) => {
                  const dateValue = params.value;
                  return dateValue ? new Date(dateValue).toLocaleDateString() : "";
                }
              },
              { field: "visit_time", headerName: "Visit Time", minWidth: 120 },
              { field: "division_name", headerName: "Division Name", minWidth: 200 },
              { field: "kerapihan_crew", headerName: "Kerapihan Crew", minWidth: 130, type: "number" },
              { field: "catatan_grooming", headerName: "Catatan Grooming", minWidth: 200, flex: 1 },
              { field: "crew_menyapa", headerName: "Crew Menyapa", minWidth: 120, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "keramahan", headerName: "Keramahan", minWidth: 100, type: "number" },
              { field: "komunikasi", headerName: "Komunikasi", minWidth: 100, type: "number" },
              { field: "responsiveness", headerName: "Responsiveness", minWidth: 130, type: "number" },
              { field: "fokus_customer", headerName: "Fokus Customer", minWidth: 130, type: "number" },
              { field: "attitude", headerName: "Attitude", minWidth: 100, type: "number" },
              { field: "catatan_behavior", headerName: "Catatan Behavior", minWidth: 200, flex: 1 },
              { field: "penyambutan", headerName: "Penyambutan", minWidth: 110, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "penjelasan", headerName: "Penjelasan", minWidth: 100, type: "number" },
              { field: "bantuan", headerName: "Bantuan", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_arahan", headerName: "Kejelasan Arahan", minWidth: 130, type: "number" },
              { field: "closing", headerName: "Closing", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_service_flow", headerName: "Catatan Service Flow", minWidth: 170, flex: 1 },
              { field: "pengetahuan_paket_harga", headerName: "Pengetahuan Harga", minWidth: 150, type: "number" },
              { field: "menawarkan_loyalty_event", headerName: "Menawarkan Loyalty", minWidth: 170, type: "number" },
              { field: "menjelaskan_fitur", headerName: "Menjelaskan Fitur", minWidth: 120, type: "number" },
              { field: "menawarkan_additional_print", headerName: "Menawarkan Additional Print", minWidth: 170, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_informasi", headerName: "Kejelasan Informasi", minWidth: 140, type: "number" },
              { field: "catatan_product_knowledge", headerName: "Catatan Product Knowledge", minWidth: 200, flex: 1 },
              { field: "kebersihan_lantai", headerName: "Kebersihan Lantai", minWidth: 120, type: "number" },
              { field: "kebersihan_booth", headerName: "Kebersihan Booth", minWidth: 120, type: "number" },
              { field: "kebersihan_properti", headerName: "Kebersihan Properti", minWidth: 120, type: "number" },
              { field: "kenyamanan_tempat", headerName: "Kenyamanan Tempat", minWidth: 120, type: "number" },
              { field: "musik", headerName: "Musik", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_kondisi_store", headerName: "Catatan Kondisi Store", minWidth: 200, flex: 1 },
              { field: "mesin_normal", headerName: "Mesin Normal", minWidth: 110, type: "number" },
              { field: "kamera_jelas", headerName: "Kamera Jelas", minWidth: 110, type: "number" },
              { field: "printer_hasil", headerName: "Printer Hasil", minWidth: 110, type: "number" },
              { field: "error", headerName: "Error", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_equipment", headerName: "Catatan Equipment", minWidth: 200, flex: 1 },
              { field: "penjelasan_flow_antrian", headerName: "Penjelasan Flow Antrian", minWidth: 150, type: "number" },
              { field: "waktu_tunggu", headerName: "Waktu Tunggu", minWidth: 110, type: "number" },
              { field: "kerapihan_antrian", headerName: "Kerapihan Antrian", minWidth: 120, type: "number" },
              { field: "pengaturan_flow", headerName: "Pengaturan Flow", minWidth: 120, type: "number" },
              { field: "catatan_antrian", headerName: "Catatan Antrian", minWidth: 200, flex: 1 },
              { field: "kepuasan_keseluruhan", headerName: "Kepuasan Keseluruhan", minWidth: 140, type: "number" },
              { field: "kembali_lagi", headerName: "Kembali Lagi", minWidth: 110, type: "number" },
              { field: "rekomendasi", headerName: "Rekomendasi", minWidth: 150, type: "number" },
              {
                field: "final_score", headerName: "Final Score", minWidth: 110, type: "number", renderCell: (params) => {
                  const num = Number(params.value);
                  return isNaN(num) ? 0 : Number(num.toFixed(2));
                }
              },
              { field: "red_flag", headerName: "Red Flag", minWidth: 120 },
              { field: "cerita_pengalaman", headerName: "Cerita Pengalaman", minWidth: 150, flex: 1 },
              {
                field: "foto_crew",
                headerName: "Foto Crew",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoCrewImages = [
                    row.foto_crew_1,
                    row.foto_crew_2,
                    row.foto_crew_3,
                    row.foto_crew_4,
                    row.foto_crew_5,
                  ].filter((img) => img);

                  return fotoCrewImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Crew - ${row.shopper_name}`, fotoCrewImages)}
                    >
                      Preview ({fotoCrewImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              {
                field: "foto",
                headerName: "Foto Store",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoImages = [
                    row.foto_1,
                    row.foto_2,
                    row.foto_3,
                    row.foto_4,
                    row.foto_5,
                    row.foto_6,
                    row.foto_7,
                    row.foto_8,
                    row.foto_9,
                    row.foto_10,
                  ].filter((img) => img);

                  return fotoImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Store - ${row.shopper_name}`, fotoImages)}
                    >
                      Preview ({fotoImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              {
                field: "voice_additional",
                headerName: "Voice Additional",
                minWidth: 150,
                sortable: false,
                renderCell: (params) => {
                  const path = params.value;
                  if (!path) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    );
                  }

                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={getMediaUrl(path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download
                    </Button>
                  );
                },
              },
              {
                field: "video_jingle",
                headerName: "Video Jingle",
                minWidth: 130,
                sortable: false,
                renderCell: (params) => {
                  const path = params.value;
                  if (!path) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    );
                  }

                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={getMediaUrl(path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download
                    </Button>
                  );
                },
              },
              {
                field: "createdtime", headerName: "Created Time", minWidth: 160, renderCell: (params) => {
                  const dateValue = params.value;
                  return dateValue ? new Date(dateValue).toLocaleString() : "";
                }
              },
              {
                field: "employees",
                headerName: "Employees",
                minWidth: 200
              },
              {
                field: "actions",
                headerName: "Action",
                minWidth: 120,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <ProtectedPermissionView acceptPermissions={[4, 5]}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openAssignEmployeesModal(params.row)}
                    >
                      Assign
                    </Button>
                  </ProtectedPermissionView>
                ),
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-cell": {
                overflow: "hidden",
              },
              ".MuiDataGrid-cellContent": {
                width: "100%",
                display: "block",
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "clip",
                scrollbarWidth: "thin",
              },
              ".MuiDataGrid-cellContent::-webkit-scrollbar": {
                height: 6,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>

      <Modal open={openImagePreview} onClose={() => setOpenImagePreview(false)}>
        <Box sx={imageModalStyle}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {imagePreviewData.title}
          </Typography>

          <Stack spacing={2}>
            {imagePreviewData.images.map((image, index) => (
              <Box key={index} sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Image {index + 1}
                </Typography>
                <img
                  src={image}
                  alt={`Preview ${index + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => setOpenImagePreview(false)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openAssignEmployees} onClose={closeAssignEmployeesModal}>
        <Box sx={{ ...imageModalStyle, width: "100%", maxWidth: 520 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Assign Employees
          </Typography>

          <FormControl fullWidth size="small">
            <FormLabel>Select Employees</FormLabel>
            <Select
              multiple
              value={selectedEmployeeIds}
              onChange={(event) => {
                const value = event.target.value;
                const values = Array.isArray(value) ? value : String(value).split(",");
                setSelectedEmployeeIds(values.map((item) => Number(item)));
              }}
              renderValue={(selected) => {
                const ids = selected as number[];
                return employeeOptions
                  .filter((employee: any) => ids.includes(Number(employee.id)))
                  .map((employee: any) => getEmployeeDisplayName(employee))
                  .join(", ");
              }}
              sx={{ mt: 1 }}
            >
              {employeeOptionsQuery.isLoading && (
                <MenuItem disabled>Loading employees...</MenuItem>
              )}

              {!employeeOptionsQuery.isLoading && employeeOptions.length === 0 && (
                <MenuItem disabled>No employee found</MenuItem>
              )}

              {employeeOptions.map((employee: any) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {getEmployeeDisplayName(employee)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={closeAssignEmployeesModal}
              disabled={assignEmployeesMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSaveAssignEmployees}
              disabled={assignEmployeesMutation.isLoading || !selectedMysteryShopperId}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>
    </MainLayout>
  );
}
